"use client";

import { useCallback, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { Message } from "@/lib/core/crm/types";
import { DraftAutoSaver, deleteDraft } from "@/lib/utils/crm/draft-storage";
import {
  extractDraftIdentifier,
  buildDraftFromFormValues,
  hasMeaningfulDraftContent,
  determineDraftType,
  buildComposeUrlParams,
} from "@/lib/utils/crm/compose-modal-utils";
import type { ComposeHookState } from "./compose-form-types";

/**
 * The slice of compose state the draft lifecycle reads. Narrower than the whole
 * `useCompose` return on purpose: the draft key and payload only depend on the
 * form values and the selected client, so nothing else can quietly become a
 * dependency.
 */
export interface ComposeDraftFormAccess {
  form: Pick<ComposeHookState["form"], "watch" | "getValues">;
  selectedClient: ComposeHookState["selectedClient"];
}

interface UseComposeDraftLifecycleArgs {
  composeForm: ComposeDraftFormAccess;
  originalMessage?: Message | null;
  initialRecipient?: string;
  /** Called after the draft is persisted, so the surface closes with work saved. */
  onOpenChange?: (open: boolean) => void;
}

interface ComposeDraftLifecycle {
  /** Storage key for this compose session's draft. Empty when there is nothing to key on. */
  draftIdentifier: string;
  /** Drop the saved draft — call once the message has actually been submitted. */
  clearSavedDraft: () => void;
  /** Close handler that saves a draft first when the form holds real content. */
  handleOpenChange: (open: boolean) => void;
  /** Save a draft, close the surface, and hand off to the full-screen composer. */
  handleGoToFullScreen: () => void;
}

/**
 * Owns ONE concern: the draft's life. Nothing here knows how the compose form is
 * rendered.
 *
 * Every way of *leaving* a compose surface without sending has to persist the
 * work first, and there are two of them: closing the surface, and navigating to
 * the full-screen composer (which unmounts this surface and rehydrates from the
 * same draft key). Both save through one `DraftAutoSaver`, keyed by
 * {@link extractDraftIdentifier} — the replied-to message id, else the intended
 * recipient — so the full-screen composer picks up exactly what the modal held.
 *
 * A draft is only written when it has meaningful content, so opening and closing
 * an untouched composer leaves no debris in storage.
 */
export function useComposeDraftLifecycle({
  composeForm,
  originalMessage,
  initialRecipient,
  onOpenChange,
}: UseComposeDraftLifecycleArgs): ComposeDraftLifecycle {
  const router = useRouter();
  const [draftAutoSaver] = useState(() => new DraftAutoSaver());

  const draftType = determineDraftType(originalMessage);
  const watchedTo = composeForm.form.watch("to");
  const draftIdentifier = useMemo(
    () => extractDraftIdentifier(originalMessage, initialRecipient, watchedTo),
    [originalMessage, initialRecipient, watchedTo]
  );

  const saveDraftIfMeaningful = useCallback(() => {
    if (!draftIdentifier) return;
    const draft = buildDraftFromFormValues(
      composeForm.form.getValues(),
      composeForm.selectedClient
    );
    if (hasMeaningfulDraftContent(draft)) {
      draftAutoSaver.forceSave(draftType, draftIdentifier, draft);
    }
  }, [
    draftIdentifier,
    draftType,
    composeForm.form,
    composeForm.selectedClient,
    draftAutoSaver,
  ]);

  const clearSavedDraft = useCallback(() => {
    if (!draftIdentifier) return;
    deleteDraft(draftType, draftIdentifier);
  }, [draftIdentifier, draftType]);

  const handleGoToFullScreen = useCallback(() => {
    saveDraftIfMeaningful();

    onOpenChange?.(false);

    const params = buildComposeUrlParams(
      originalMessage,
      initialRecipient,
      composeForm.form.getValues("to"),
      composeForm.selectedClient?.id
    );

    router.push(`/crm/messages/compose?${params.toString()}`);
  }, [
    saveDraftIfMeaningful,
    composeForm.form,
    composeForm.selectedClient,
    onOpenChange,
    router,
    originalMessage,
    initialRecipient,
  ]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        saveDraftIfMeaningful();
      }
      onOpenChange?.(open);
    },
    [onOpenChange, saveDraftIfMeaningful]
  );

  return { draftIdentifier, clearSavedDraft, handleOpenChange, handleGoToFullScreen };
}
